import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Eye,
  ExternalLink,
  TrendingUp,
  Loader2,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { TopPost, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface PostPerformanceTableProps {
  posts: TopPost[];
  isLoading: boolean;
}

export const PostPerformanceTable: React.FC<PostPerformanceTableProps> = ({
  posts,
  isLoading
}) => {
  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const getTotalEngagement = (post: TopPost) => {
    return (
      (post.analytics.likes_count || 0) +
      (post.analytics.comments_count || 0) +
      (post.analytics.shares_count || 0) +
      (post.analytics.saves_count || 0)
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg">Top Performing Posts</CardTitle>
            <CardDescription>Your best posts ranked by engagement</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : posts.length === 0 ? (
          <div className="h-[200px] flex flex-col items-center justify-center">
            <FileText className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No published posts in this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Post</TableHead>
                  <TableHead className="w-[100px]">Platform</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Heart className="h-3 w-3" />
                      Likes
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <MessageCircle className="h-3 w-3" />
                      Comments
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Share2 className="h-3 w-3" />
                      Shares
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Eye className="h-3 w-3" />
                      Reach
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((topPost, index) => {
                  const Icon = getPlatformIcon(topPost.platform);
                  const platformInfo = PLATFORM_INFO[topPost.platform];
                  const publishResult = topPost.post.publish_results?.[topPost.platform];

                  return (
                    <TableRow key={`${topPost.post.id}-${topPost.platform}`}>
                      <TableCell className="font-medium">
                        <Badge
                          variant={index < 3 ? 'default' : 'secondary'}
                          className={cn(
                            "w-6 h-6 rounded-full p-0 flex items-center justify-center",
                            index === 0 && "bg-yellow-500",
                            index === 1 && "bg-gray-400",
                            index === 2 && "bg-amber-600"
                          )}
                        >
                          {index + 1}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {topPost.post.media_urls?.[0] && (
                            <img
                              src={topPost.post.media_urls[0]}
                              alt="Post thumbnail"
                              className="h-10 w-10 rounded object-cover flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm line-clamp-2 max-w-[300px]">
                              {topPost.post.content}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {topPost.post.published_at
                                ? format(new Date(topPost.post.published_at), 'MMM d, yyyy')
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <div
                            className="rounded-full p-1"
                            style={{ backgroundColor: platformInfo.bgColor }}
                          >
                            <Icon
                              className="h-3 w-3"
                              style={{ color: platformInfo.color }}
                            />
                          </div>
                          <span className="text-sm">{platformInfo.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(topPost.analytics.likes_count || 0)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(topPost.analytics.comments_count || 0)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(topPost.analytics.shares_count || 0)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(topPost.analytics.reach || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className="font-bold">
                          {formatNumber(getTotalEngagement(topPost))}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {publishResult?.url && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <a
                              href={publishResult.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PostPerformanceTable;
